import React, { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeControlPhone } from '@fortawesome/free-solid-svg-icons';
import AudioModal from 'components/audio/AudioModal';
import Avatar from 'components/base/Avatar';
import { format, parse } from 'date-fns';

type Call = {
  servicename: string;
  servicebrand: string;
  campaignname: string;
  campaignid: number;
  callid: string;
  agent: string;
  accountref: string;
  customerref: string;
  customerfullname: string;
  phonenumber: string;
  calldatetime: string;
  duration: string;
  audiosource: string;
};

export const getRecordingsTableColumns = (
  onModalStateChange?: (isOpen: boolean) => void
): ColumnDef<Call>[] => [
  {
    accessorKey: 'campaignname',
    header: 'Campaign',
    cell: ({ row: { original } }) => {
      const { campaignname, servicebrand } = original;
      const [imageSrc, setImageSrc] = useState(`/brands/${servicebrand}.png`);

      const handleImageError = () => {
        setImageSrc('/brands/default.png');
      };

      return (
        <div className="d-flex align-items-center text-body-emphasis">
          {imageSrc && (
            <Avatar
              src={imageSrc}
              size="s"
              alt={campaignname}
              onError={handleImageError}
            />
          )}
          &nbsp;{campaignname}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' }, className: 'pe-2 ps-0' },
      cellProps: { className: 'p-0 fw-bold white-space-nowrap' }
    }
  },
  {
    accessorKey: 'callid',
    header: 'Call ID',
    meta: {
      headerProps: { style: { width: '12%' }, className: 'px-1' },
      cellProps: { className: 'text-body px-1' }
    }
  },
  {
    accessorKey: 'agent',
    header: 'HVA',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'text-body px-1' }
    }
  },
  {
    accessorKey: 'accountref',
    header: 'Account ID',
    meta: {
      headerProps: { style: { width: '7%' }, className: 'px-1' },
      cellProps: { className: 'text-body px-1' }
    }
  },
  {
    accessorKey: 'customerref',
    header: 'Customer ID',
    meta: {
      headerProps: { style: { width: '7%' }, className: 'px-1' },
      cellProps: { className: 'text-body px-1' }
    }
  },
  {
    accessorKey: 'customerfullname',
    header: 'Customer Name',
    meta: {
      headerProps: { style: { width: '12%' }, className: 'px-1' },
      cellProps: {
        className: 'text-body px-1 white-space-nowrap'
      }
    }
  },
  {
    accessorKey: 'phonenumber',
    header: 'Phone #',
    meta: {
      headerProps: { style: { width: '8%' }, className: 'px-1' },
      cellProps: {
        className: 'text-body px-1 white-space-nowrap'
      }
    }
  },
  {
    accessorKey: 'calldatetime',
    header: 'Call date & time',
    cell: ({ row: { original } }) => {
      // Parse the date string in the format dd-mm-yyyy hh:mm:ss
      const parsedDate = parse(
        original.calldatetime,
        'dd-MM-yyyy HH:mm:ss',
        new Date()
      );
      const formattedDate = format(parsedDate, 'MMM dd, yyyy - HH:mm:ss');
      return <span>{formattedDate}</span>;
    },
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1' },
      cellProps: { className: 'text-body px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'duration',
    header: 'Duration (sec.)',
    meta: {
      headerProps: { style: { width: '9%' }, className: 'px-1' },
      cellProps: {
        className: 'text-body d-flex justify-content-center'
      }
    }
  },
  {
    header: 'Recording',
    cell: ({ row: { original } }) => {
      const [showModal, setShowModal] = useState(false);

      const handleIconClick = () => {
        setShowModal(true);
        onModalStateChange?.(true);
      };

      const handleCloseModal = () => {
        setShowModal(false);
        onModalStateChange?.(false);
      };

      const audioSrc = original.audiosource;
      const campaignId = original.campaignid;
      const callId = original.callid;
      const customerName = original.customerfullname;
      const parsedDate = parse(
        original.calldatetime,
        'dd-MM-yyyy HH:mm:ss',
        new Date()
      );
      const formattedDate =
        format(parsedDate, 'MMM dd, yyyy - HH:mm:ss') +
        ' (Phone number: ' +
        original.phonenumber +
        ')';

      return (
        <>
          <FontAwesomeIcon
            className="text-primary cursor-pointer"
            icon={faVolumeControlPhone}
            size="lg"
            onClick={handleIconClick}
          />
          <AudioModal
            show={showModal}
            onHide={handleCloseModal}
            audioSrc={audioSrc}
            campaignId={campaignId}
            callId={callId}
            customer={customerName}
            calldata={formattedDate}
          />
        </>
      );
    },
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-3' }
    }
  }
];

export const RecordingsTable = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-staticm"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
